import React, { useState, useEffect } from 'react';
import { DataTable } from '@scuf/datatable';

import './OrganisationsList.scss';
import { ICellData } from '../onboardingUtils';
import { pascalToCamelCase } from '../onboardingUtils';

import { consumeAPIs, IAPIPackage } from '../../commonUtils/APIContext';
import useFetch, { IUseFetch } from '../../commonUtils/useFetch';
import {
  IOrganisationResponse,
  IOrganisationSiteAndUserCount2,
  INodeMetadataResponse,
  INodeMetadata
} from '../onboardingModels';

import { SplitPanelUtil } from '../../commonUtils/SplitPanelUtil';
import toasts from '../../commonUtils/toasts';
import i18next from '../../../i18next';

export interface IOrganisationBasicData {
  organisationId: string;
  organisationName: string;
}

interface IOrganisationsList extends IAPIPackage {}

export const OrganisationsList = (props: IOrganisationsList) => {
  const [
    organisationData,
    isLoadingOrganisationData,
    errorOrganisationData,
    fetchOrganisationData
  ]: IUseFetch<IOrganisationResponse> = useFetch(
    `${props.baseURL}/api/RootOrganisations/${props.offering}`,
    props.authFetch,
    undefined,
    true
  );

  useEffect(() => {
    fetchOrganisationData();
  }, []);

  useEffect(() => {
    if (errorOrganisationData) {
      toasts.error(i18next.t('organisationListError'));
    }
  }, [errorOrganisationData]);

  const [nodeMetaData, setNodeMetadata] = useState<INodeMetadata[]>([]);

  useEffect(() => {
    if (organisationData && organisationData.resources) {
      lazyLoadMetadata('1', 9);
    }
  }, [organisationData]);

  const fetchRootNodeMetaData = (rootNodeId: string) => {
    props
      .authFetch(
        `${props.baseURL}/api/v1/metadata/${props.offering}/${rootNodeId}`
      )
      .then(res => {
        if (res.ok) {
          return res;
        } else {
          throw new Error(i18next.t('hierarchyMetaError'));
        }
      })
      .then(res => res.json() as Promise<INodeMetadataResponse>)
      .then(metaData => {
        // console.log('Metadata: ', metaData);
        const metaDataObject: INodeMetadata = {
          id: rootNodeId,
          data: metaData
        };
        setNodeMetadata(prevMetadataState => [
          ...prevMetadataState,
          metaDataObject
        ]);
      })
      .catch(e => console.error(`${i18next.t('nodeMetaError')}: ${e}`));
  };

  const injectMetaData = (orgData: IOrganisationResponse) => {
    const formatedTableData =
      orgData && orgData.resources
        ? orgData.resources.map(node => {
            const matchingMetaData = nodeMetaData.find(
              nodeMetaDatum => node.id === nodeMetaDatum.id
            );

            const tableReadyMetadata = matchingMetaData
              ? matchingMetaData.data.reduce(
                  (ob, currentVal) => ({
                    ...ob,
                    [pascalToCamelCase(currentVal.key)]: currentVal.value
                  }),
                  {}
                )
              : {};

            const injectedNode = { ...node, ...tableReadyMetadata };

            return injectedNode;
          })
        : [];
    return formatedTableData;
  };

  const lazyLoadMetadata = (
    activePage: React.ReactText,
    itemsPerPage?: string | number | undefined
  ) => {
    const pageIndex = +activePage - 1;
    const numberOfItemsPerPage = itemsPerPage ? +itemsPerPage : 0;
    [...organisationData.resources]
      .splice(pageIndex * numberOfItemsPerPage, numberOfItemsPerPage)
      .forEach(rootNode => {
        fetchRootNodeMetaData(rootNode.id);
      });
  };

  return (
    <div className="organisations-list-view ">
      <div className="page-header-bar">
        <div className="page-header-bar-heading">
          {/* <Icon className="icon" root="common" name="globe" size="small" />  */}
          {i18next.t('allOrganisations')}
        </div>
        <div className="page-header-bar-controls">
          {/* <div className="header-stat">
            <div className="header-stat-value">0</div>
            <div className="header-stat-name">Organisations</div>
          </div>
          <div className="header-stat">
            <div className="header-stat-value">0</div>
            <div className="header-stat-name">Nodes</div>
          </div>
          <div className="header-stat">
            <div className="header-stat-value">0</div>
            <div className="header-stat-name">Users</div>
          </div> */}
        </div>
      </div>
      <div className="organisations-list-view-content page-content-container with-header">
        <SplitPanelUtil>
          <SplitPanelUtil.Table>
            <DataTable
              // Loading icons here don't work properly
              // loading={true}
              loading={isLoadingOrganisationData}
              // virtualRowHeight={48}
              className="organisations-list-view-table "
              data={injectMetaData(organisationData)}
              emptyMessage={i18next.t('noRecordFound')}
              search={true}
              searchPlaceholder={i18next.t('searchFor')}
              onCellClick={(
                cellData: ICellData<IOrganisationSiteAndUserCount2>
              ) => {
                // Currently disabled until we can do routing to the org hierarchy
                // setSelectedOrganisation({
                //   organisationId: cellData.rowData.nodeId,
                //   organisationName: cellData.rowData.name
                // });
              }}
            >
              <DataTable.HeaderBar>
                {/* <DataTable.HeaderBar.FilterPanel count={'6'} onReset={() => {}}>
                  <DataTable.HeaderBar.FilterPanel.Section label="Status">
                    <DataTable.HeaderBar.FilterPanel.Item>
                      <DataTable.HeaderBar.FilterPanel.ToggleItem
                        type="checkbox"
                        label="Creating"
                        active={true}
                        onChange={() => {}}
                      />
                      <DataTable.HeaderBar.FilterPanel.ToggleItem
                        type="checkbox"
                        label="Create failed"
                        active={true}
                        onChange={() => {}}
                      />
                      <DataTable.HeaderBar.FilterPanel.ToggleItem
                        type="checkbox"
                        label="Created"
                        active={true}
                        onChange={() => {}}
                      />
                    </DataTable.HeaderBar.FilterPanel.Item>
                  </DataTable.HeaderBar.FilterPanel.Section>
                  <DataTable.HeaderBar.FilterPanel.Section label="Configuration">
                    <DataTable.HeaderBar.FilterPanel.ToggleItem
                      type="checkbox"
                      label="Primary contact"
                      active={true}
                      onChange={() => {}}
                    />
                    <DataTable.HeaderBar.FilterPanel.ToggleItem
                      type="checkbox"
                      label="Unverified user(s)"
                      active={true}
                      onChange={() => {}}
                    />
                    <DataTable.HeaderBar.FilterPanel.ToggleItem
                      type="checkbox"
                      label="Verified user(s)"
                      active={true}
                      onChange={() => {}}
                    />
                  </DataTable.HeaderBar.FilterPanel.Section>
                </DataTable.HeaderBar.FilterPanel> */}
              </DataTable.HeaderBar>
              <DataTable.Column field="name" header={i18next.t('name')} />

              <DataTable.Column field="type" header={i18next.t('type')} />
              <DataTable.Column field="address" header={i18next.t('address')} />
              <DataTable.Column field="status" header={i18next.t('status')} />
              <DataTable.Pagination
                totalItems={
                  organisationData ? organisationData.resources.length : 0
                }
                itemsPerPage={9}
                onPageChange={lazyLoadMetadata}
              />
            </DataTable>
          </SplitPanelUtil.Table>
        </SplitPanelUtil>
      </div>
    </div>
  );
};

export default consumeAPIs(OrganisationsList);
